/* ============================================================
 * Learn2Ride — Judge.me lazy-load fix
 *
 * Why this exists:
 *   The Judge.me Review Widget on /products/learn2ride-reviews
 *   (and any other product page that renders the Review Widget)
 *   lazy-loads each <img class="jdgm-rev__pic-img"> by storing the
 *   real URL in `data-src` and giving the parent `.jdgm-rev__pic-link`
 *   a `.jdgm--loading` class. Their default CSS does:
 *     .jdgm-rev__pic-link.jdgm--loading .jdgm-rev__pic-img {
 *       visibility: hidden;
 *     }
 *   Their JS is supposed to (a) copy data-src → src, (b) listen for
 *   the image's `load` event, and (c) remove the `.jdgm--loading`
 *   class from the link.
 *
 *   On the L2R dev theme that init step never fires reliably —
 *   verified live 2026-05-04 via DevTools: jdgm.run is undefined,
 *   `.jdgm--loading` never gets removed, every photo stays at
 *   visibility:hidden with naturalWidth=0 forever. Result:
 *   reviews look like they have no photos.
 *
 *   This shim picks up where their init left off:
 *     1. For every .jdgm-rev__pic-img with data-src and no src,
 *        copy data-src into src so the browser fetches the image.
 *     2. When each image's load event fires, remove .jdgm--loading
 *        from the closest .jdgm-rev__pic-link so the image becomes
 *        visible.
 *     3. Run on DOMContentLoaded for the initial page render, then
 *        again whenever new .jdgm-rev nodes appear (Judge.me swaps
 *        the review list HTML on pagination clicks — a MutationObserver
 *        on the reviews container catches that).
 *
 *   No-op if there are no jdgm photos on the page (so it's safe to
 *   load globally from theme.liquid).
 * ============================================================ */

(function () {
  'use strict';

  /**
   * Judge.me serves review photos through imgix (judgeme.imgix.net).
   * Their default URL ships at ~160px wide with no crop hints — blurry
   * in our larger sticker frames, and a naive center crop that clips
   * faces on portrait photos. Substituting our params gives a high-DPI
   * 4:3 output with face-aware cropping.
   *
   * Why split-and-substitute: imgix uses first-occurrence-wins for
   * duplicate query keys, so appending `&w=1000` to a URL that already
   * has `?w=160` does nothing — the small width still wins. We strip
   * the existing query string and rebuild it.
   *
   * No signed token (`s=`) is on Judge.me's URLs (verified 2026-05-01),
   * so dropping the original query is safe. Non-imgix hosts pass
   * through untouched.
   *
   * Mirrors the same transform used by sections/l2r-testimonials.liquid.
   */
  function withImgixCrop(url) {
    if (!url || url.indexOf('imgix.net') === -1) return url;
    var basePath = url.split('?')[0];
    return basePath + '?w=1000&h=750&fit=crop&crop=faces,entropy&auto=format,compress&q=85';
  }

  function unhideOnLoad(img) {
    var link = img.closest('.jdgm-rev__pic-link');
    if (link) link.classList.remove('jdgm--loading');
  }

  function hydratePhoto(img) {
    if (!img || img.dataset.l2rHydrated) return;
    img.dataset.l2rHydrated = '1';

    var dataSrc = img.getAttribute('data-src');
    var hasSrc = img.hasAttribute('src') && img.getAttribute('src');

    // If already loaded (Judge.me beat us to it), just clear the
    // loading class and we're done.
    if (hasSrc && img.naturalWidth > 0) {
      unhideOnLoad(img);
      return;
    }

    // Wire up the load handler before setting src so we never miss
    // the event for fast-loading (cached) images.
    img.addEventListener('load', function () { unhideOnLoad(img); }, { once: true });
    img.addEventListener('error', function () { unhideOnLoad(img); }, { once: true });

    if (!hasSrc && dataSrc) {
      // Apply face-focus imgix transform on the way in.
      img.src = withImgixCrop(dataSrc);
    } else if (hasSrc && img.complete) {
      // src present but load event already fired before we attached.
      unhideOnLoad(img);
    }
  }

  function hydrateAll(root) {
    var scope = root || document;
    var imgs = scope.querySelectorAll('.jdgm-rev__pic-img');
    for (var i = 0; i < imgs.length; i++) hydratePhoto(imgs[i]);
  }

  /**
   * Round the rating display from "5.00 out of 5" → "5 out of 5".
   *
   * Judge.me always renders the average rating with two decimals
   * (`5.00`, `4.83`, etc.) inside `<span class="jdgm-rev-widg__summary-average">`.
   * For L2R the trailing `.00` is noise — every review is 5 stars so
   * the average is always `5.00`. We round the float back to an integer
   * when it has no fractional part, otherwise leave a sensible rounded
   * decimal. Example transforms:
   *   "5.00 out of 5" → "5 out of 5"
   *   "4.83 out of 5" → "4.8 out of 5"
   *
   * Idempotent — safe to re-run whenever Judge.me re-renders the
   * summary block (re-paginations don't typically swap this element,
   * but the MutationObserver below catches it anyway).
   */
  function roundRatingText() {
    var avgEls = document.querySelectorAll('.jdgm-rev-widg__summary-average');
    for (var i = 0; i < avgEls.length; i++) {
      var el = avgEls[i];
      if (el.dataset.l2rRounded) continue;
      var txt = el.textContent;
      // Match an N.NN at the start of the text and round it
      var m = txt.match(/^\s*(\d+)\.(\d+)/);
      if (!m) continue;
      var whole = m[1];
      var frac = parseInt(m[2], 10);
      var replacement;
      if (frac === 0) {
        replacement = whole;                    // 5.00 → 5
      } else {
        replacement = whole + '.' + Math.round(frac / 10); // 4.83 → 4.8
      }
      el.textContent = txt.replace(/^\s*\d+\.\d+/, replacement);
      el.dataset.l2rRounded = '1';
    }
  }

  function init() {
    hydrateAll(document);
    roundRatingText();

    // Watch for Judge.me swapping in new reviews on pagination clicks.
    // The widget container itself stays in place; only its inner
    // .jdgm-rev-widg__reviews list is replaced.
    var widg = document.querySelector('.jdgm-rev-widg');
    if (widg && 'MutationObserver' in window) {
      var obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('.jdgm-rev__pic-img')) {
              hydratePhoto(node);
            } else if (node.querySelectorAll) {
              hydrateAll(node);
            }
          }
        }
        roundRatingText();
      });
      obs.observe(widg, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run shortly after init too — Judge.me sometimes injects the
  // review HTML asynchronously a fraction of a second after their
  // bundle fires. Three retries at 200ms / 800ms / 2000ms is enough
  // to catch the slowest paint. roundRatingText runs alongside in
  // case the summary block paints late.
  setTimeout(function () { hydrateAll(document); roundRatingText(); }, 200);
  setTimeout(function () { hydrateAll(document); roundRatingText(); }, 800);
  setTimeout(function () { hydrateAll(document); roundRatingText(); }, 2000);
})();
